import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Award,
    Heart,
    Handshake,
    Eye,
    Target,
    Globe2,
    Sparkles,
} from "lucide-react";
import { Card } from "../components/ui/card";

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="about-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-32 right-1/4 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">About AccessNova</p>
            <h1 id="about-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Our Mission: A Digitally Inclusive World
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                AccessNova exists to make the digital world usable by everyone — regardless of ability.
            </p>
        </div>
    </section>
);

/* ---------------- FOUNDER ---------------- */
const FounderAvatar = () => (
    <svg viewBox="0 0 240 240" className="w-full h-full" role="img" aria-label="Founder portrait placeholder">
        <defs>
            <linearGradient id="founder-bg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#0A2540" />
                <stop offset="100%" stopColor="#127A75" />
            </linearGradient>
        </defs>
        <circle cx="120" cy="120" r="120" fill="url(#founder-bg)" />
        <circle cx="120" cy="100" r="38" fill="#FFFFFF" opacity="0.92" />
        <path
            d="M50 220 C60 170 90 150 120 150 C150 150 180 170 190 220 Z"
            fill="#FFFFFF"
            opacity="0.92"
        />
    </svg>
);

const Founder = () => (
    <section aria-labelledby="founder-heading" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-4">
                    <div className="relative w-56 h-56 sm:w-64 sm:h-64 mx-auto lg:mx-0 rounded-full overflow-hidden ring-4 ring-brand-soft shadow-cardHover">
                        <FounderAvatar />
                    </div>
                    <p className="mt-4 text-center lg:text-left text-sm font-semibold text-brand-text">
                        Founder &amp; Principal Auditor
                    </p>
                    <p className="text-center lg:text-left text-xs text-brand-muted">AccessNova · India</p>
                </div>

                <div className="lg:col-span-8">
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Founder story</p>
                    <h2 id="founder-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                        Twelve years inside the work — auditing, fixing, advocating.
                    </h2>
                    <div className="mt-6 space-y-4 text-base text-brand-body leading-relaxed">
                        <p>
                            AccessNova was founded with 12+ years of hands-on accessibility experience auditing,
                            remediating, and consulting for global businesses across e-commerce, banking,
                            healthcare, education, and government. Long before accessibility became a board-level
                            conversation, our team was building tagged PDFs, fighting for keyboard-first UX, and
                            translating WCAG criteria into engineering tickets.
                        </p>
                        <p>
                            We started AccessNova because too many accessibility consultancies hand clients a PDF
                            of violations and walk away. We don't. We sit with engineering teams, write the fixes,
                            retest after every release, and stay until the report is irrelevant — because the
                            product is just accessible.
                        </p>
                        <p>
                            Accessibility isn't a checkbox or a legal risk. It's how 1.3 billion people with
                            disabilities get to use the internet. That's why we wake up. That's the only thing
                            we do.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ---------------- HOW WE WORK + STATS ---------------- */
const HOW_BULLETS = [
    "Specialized accessibility experts only — no generalists, no side projects.",
    "Manual + automated testing methodology, validated against real assistive tech.",
    "India-headquartered, globally distributed remote team.",
    "Timezone advantage — overnight audits, morning-ready reports for North America.",
    "Cost-effective expertise without compromising depth or quality.",
];

const STATS = [
    { icon: Award, label: "12+", sub: "Years Experience" },
    { icon: Sparkles, label: "500+", sub: "Audits Delivered" },
    { icon: Target, label: "7+", sub: "Compliance Standards Mastered" },
    { icon: Globe2, label: "4", sub: "Continents Served" },
];

const HowWeWork = () => (
    <section aria-labelledby="how-heading" className="bg-brand-soft border-y border-brand-border py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Our approach</p>
                    <h2 id="how-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                        How we work
                    </h2>
                    <ul className="mt-8 space-y-4">
                        {HOW_BULLETS.map((b, i) => (
                            <li key={i} className="flex gap-3">
                                <span aria-hidden="true" className="flex-none w-6 h-6 rounded-full bg-brand-teal text-white inline-flex items-center justify-center text-xs font-bold">
                                    {i + 1}
                                </span>
                                <span className="text-base text-brand-body leading-relaxed">{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <Card className="p-8 bg-white border-brand-border shadow-cardHover" data-testid="about-stats-card">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">By the numbers</p>
                    <ul className="mt-6 grid grid-cols-2 gap-6">
                        {STATS.map(({ icon: Icon, label, sub }) => (
                            <li key={sub} className="flex flex-col gap-2">
                                <Icon size={22} className="text-brand-teal" aria-hidden="true" />
                                <span className="text-3xl font-bold text-brand-text">{label}</span>
                                <span className="text-xs font-medium text-brand-muted leading-tight">{sub}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            </div>
        </div>
    </section>
);

/* ---------------- VALUES ---------------- */
const VALUES = [
    { icon: Award, title: "Excellence", desc: "Audits that find what others miss. Manual expert review behind every automated scan." },
    { icon: Heart, title: "Inclusion", desc: "Accessibility is a human right. We treat it that way — not as a checklist." },
    { icon: Handshake, title: "Partnership", desc: "We work alongside, not just for, our clients. Your engineers and our auditors, on the same call." },
    { icon: Eye, title: "Transparency", desc: "Clear pricing, clear reports, clear outcomes. No jargon. No surprise invoices." },
];

const Values = () => (
    <section aria-labelledby="values-heading" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">What we stand for</p>
                <h2 id="values-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                    Values that show up in the work.
                </h2>
            </div>
            <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                {VALUES.map((v, i) => {
                    const Icon = v.icon;
                    return (
                        <li key={v.title} data-testid={`value-card-${i}`}>
                            <Card className="h-full p-7 border-brand-border bg-white hover:shadow-cardHover transition-shadow flex gap-5">
                                <div className="flex-none w-12 h-12 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center">
                                    <Icon size={24} aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-brand-text">{v.title}</h3>
                                    <p className="mt-2 text-sm text-brand-body leading-relaxed">{v.desc}</p>
                                </div>
                            </Card>
                        </li>
                    );
                })}
            </ul>
        </div>
    </section>
);

/* ---------------- CERTIFICATIONS ---------------- */
const CERTS = [
    { name: "IAAP Member", sub: "Int'l Assoc. of Accessibility Professionals" },
    { name: "WCAG 2.2 Specialist", sub: "Certified auditors on every engagement" },
    { name: "PDF/UA (ISO 14289)", sub: "Tagged PDF conformance" },
    { name: "EN 301 549", sub: "EU accessibility standard" },
    { name: "Section 508", sub: "Trusted Tester methodology" },
    { name: "BITV-Test", sub: "German public sector certification" },
];

const Certifications = () => (
    <section aria-labelledby="certs-heading" className="bg-brand-soft border-y border-brand-border py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm font-semibold uppercase tracking-wider text-brand-teal">Certifications &amp; memberships</p>
            <h2 id="certs-heading" className="mt-2 text-center text-2xl sm:text-3xl font-bold text-brand-text max-w-3xl mx-auto">
                Credentialed where it matters.
            </h2>
            <ul className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {CERTS.map((c) => (
                    <li key={c.name}>
                        <div
                            className="h-full bg-white border border-brand-border rounded-lg p-5 text-center transition-all duration-200 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 hover:shadow-card focus-within:grayscale-0 focus-within:opacity-100"
                            data-testid={`cert-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                            <p className="text-sm font-bold text-brand-navy leading-tight">{c.name}</p>
                            <p className="mt-2 text-xs text-brand-muted leading-snug">{c.sub}</p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* ---------------- CTA ---------------- */
const CTA = () => (
    <section aria-labelledby="about-cta" className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 id="about-cta" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-text">
                Ready to make your platform accessible?
            </h2>
            <p className="mt-4 text-lg text-brand-body">
                Start with a free 1-page snapshot — or talk to us about a full audit.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                    to="/free-audit"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target"
                    data-testid="about-cta-audit"
                >
                    Get a Free Mini Audit
                    <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-md border-2 border-brand-navy text-brand-navy font-semibold px-6 py-3 hover:bg-brand-navy hover:text-white transition-colors duration-200 touch-target"
                    data-testid="about-cta-contact"
                >
                    Talk to Us
                </Link>
            </div>
        </div>
    </section>
);

/* ---------------- PAGE ---------------- */
export default function About() {
    useEffect(() => {
        document.title = "About — AccessNova";
    }, []);

    return (
        <>
            <Hero />
            <Founder />
            <HowWeWork />
            <Values />
            <Certifications />
            <CTA />
        </>
    );
}
